import { atom } from 'jotai'
import { RESET } from 'jotai/utils'

import { imageUrlToFile, isOnServer } from '@/utils'

import type { TQrDesign } from '../../services/types'
import {
	cornerDotColorAtom,
	cornerDotStyleAtom,
	cornerFrameColorAtom,
	cornerFrameStyleAtom,
} from './CornersDesignForm'
import {
	frameBackgroundColorAtom,
	frameColorAtom,
	frameTextAtom,
	frameTextColorAtom,
	frameTypeAtom,
} from './FrameDesignForm'
import {
	qrLogoImageAtom,
	qrLogoImageFileAtom,
	qrSelectedLogoUrlAtom,
} from './LogoDesignForm'
import {
	patternAtom,
	qrBackgroundColorAtom,
	qrDotColorAtom,
} from './PatternDesignForm'

// Atom for tracking qrDesign null state internally and should not be exported to other modules.
const isQrDesignNullAtom = atom(true)
export const qrDesignImageErrorAtom = atom<Error | null>(null)

export const qrDesignAtom = atom<TQrDesign | null, [TQrDesign | null], void>(
	(get) => {
		const isNull = get(isQrDesignNullAtom)
		if (isNull) {
			return null
		}
		return {
			frame: {
				type: get(frameTypeAtom),
				text: get(frameTextAtom),
				textColor: get(frameTextColorAtom),
				color: get(frameColorAtom),
				background: get(frameBackgroundColorAtom),
			},
			dotsOptions: {
				type: get(patternAtom),
				color: get(qrDotColorAtom),
			},
			backgroundOptions: {
				color: get(qrBackgroundColorAtom),
			},
			cornersSquareOptions: {
				type: get(cornerFrameStyleAtom),
				color: get(cornerFrameColorAtom),
			},
			cornersDotOptions: {
				type: get(cornerDotStyleAtom),
				color: get(cornerDotColorAtom),
			},
			image: {
				selected: get(qrSelectedLogoUrlAtom),
				uploaded: get(qrLogoImageAtom),
			},
		} as TQrDesign
	},
	(_, set, qrDesign: TQrDesign | null) => {
		set(isQrDesignNullAtom, !qrDesign)
		set(frameTypeAtom, qrDesign?.frame?.type || RESET)
		set(frameTextAtom, qrDesign?.frame?.text || RESET)
		set(frameTextColorAtom, qrDesign?.frame?.textColor || RESET)
		set(frameColorAtom, qrDesign?.frame?.color || RESET)
		set(frameBackgroundColorAtom, qrDesign?.frame?.background || RESET)
		set(patternAtom, qrDesign?.dotsOptions?.type || RESET)
		set(qrDotColorAtom, qrDesign?.dotsOptions?.color || RESET)
		set(qrBackgroundColorAtom, qrDesign?.backgroundOptions?.color || RESET)
		set(cornerDotStyleAtom, qrDesign?.cornersDotOptions?.type || RESET)
		set(cornerDotColorAtom, qrDesign?.cornersDotOptions?.color || RESET)
		set(cornerFrameStyleAtom, qrDesign?.cornersSquareOptions?.type || RESET)
		set(
			cornerFrameColorAtom,
			qrDesign?.cornersSquareOptions?.color || RESET,
		)
		set(qrSelectedLogoUrlAtom, qrDesign?.image?.selected || RESET)
		set(qrLogoImageAtom, qrDesign?.image?.uploaded || RESET)

		if (!isOnServer) {
			setTimeout(async () => {
				try {
					if (qrDesign?.image?.uploaded?.publicId?.length) {
						const file = await imageUrlToFile(
							`${qrDesign.image?.uploaded.bucketRootUrl}/${qrDesign.image?.uploaded.publicId}`,
							qrDesign.image?.uploaded.publicId,
							qrDesign.image?.uploaded.resourceType,
						)
						set(qrLogoImageFileAtom, file)
					}
				} catch (error) {
					set(qrDesignImageErrorAtom, error as Error)
				}
			}, 0)
		}
	},
)
